import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon, MoreVerticalIcon, TrashIcon } from '@hero/web-icons';
import type {
  RouteRegion,
  TripLoadType,
  TripType,
  VehicleTypes,
} from '@hero/schema';
import type { RegionCity } from '@hero/api';
import { deriveTemperatureFromVehicles } from './deriveTemperature';
import { cn } from '@hero/web-lib';
import { Checkbox } from '../../../ui/Checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/DropdownMenu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/Select';
import { PriceEdit } from '../../../app/Price/PriceEdit';
import { RegionSelector } from '../../Region/RegionSelector';
import { VehicleTypeMultiSelector } from '../../Vehicle/VehicleTypeMultiSelector';
import { usePricingSheetStore } from './store/usePricingSheetStore';
import type { SheetRow, SheetSide } from './types';

export interface PricingSheetRowProps {
  row: SheetRow;
  /** Active pricing side — controls rate cell tinting. */
  side: SheetSide;
  isSelected: boolean;
}

/**
 * One editable row inside the price sheet. All fields are inline-editable; the
 * row tracks `status` so the parent table can render badges (new / modified)
 * and gate "Save all" accordingly.
 *
 * Density target: 32px row content height, 12px font, no bold weights inside
 * cells — the sheet should read like a spreadsheet, not a stack of forms.
 */
export function PricingSheetRow({ row, side, isSelected }: PricingSheetRowProps) {
  const { t } = useTranslation(['m-pricing', 'm-route', 'c-main']);

  const changeRow = usePricingSheetStore(s => s.changeRow);
  const toggleRow = usePricingSheetStore(s => s.toggleRow);
  const duplicateRow = usePricingSheetStore(s => s.duplicateRow);
  const deleteRow = usePricingSheetStore(s => s.deleteRow);

  const onChange = (patch: Partial<SheetRow>) => changeRow(row.id, patch);

  const handleRegion =
    (key: 'origin' | 'destination') =>
    (region: RegionCity | null) => {
      const next: RouteRegion | null = region
        ? ({
            _id: region._id,
            displayName: region.displayName,
            customCode: region.customCode,
            type: 'city',
          } as unknown as RouteRegion)
        : null;
      // Status is owned by the store — it preserves `'new'` so a row that
      // was just added doesn't get bumped to `'modified'` and routed to the
      // update endpoint without a `routeId`.
      onChange({ [key]: next } as Partial<SheetRow>);
    };

  const rowToneClass =
    row.status === 'deleted'
      ? 'bg-red-50/40 opacity-50'
      : row.status === 'new'
        ? 'bg-emerald-50/40'
        : row.status === 'modified'
          ? 'bg-amber-50/40'
          : isSelected
            ? 'bg-primary/5'
            : 'bg-white hover:bg-gray-50/60';

  return (
    <tr
      className={cn(
        'group border-b border-gray-100 transition-colors align-middle',
        rowToneClass,
      )}>
      <td className="px-3 py-1.5 align-middle">
        <Checkbox
          checked={isSelected}
          onCheckedChange={v => toggleRow(row.id, !!v)}
        />
      </td>

      <td className="px-1.5 py-1 align-middle">
        <RegionSelector
          size="sm"
          hideClear
          types={['city']}
          value={row.origin ? String(row.origin._id) : null}
          onChange={handleRegion('origin')}
          placeholder={t('sheet.row.origin_placeholder', { defaultValue: 'Origin' })}
          drawerTitle={t('sheet.row.origin_drawer_title', {
            defaultValue: 'Pick origin city',
          })}
          className="min-w-[130px]"
        />
      </td>

      {/*
        Directional arrow column — mirrors the filter strip so the row
        reads "origin → destination" at a glance. Decorative only;
        `aria-hidden` keeps it out of the accessibility tree.
      */}
      <td aria-hidden className="w-[24px] px-0 align-middle text-center">
        <ArrowRightIcon className="mx-auto size-3 text-gray-300 rtl:rotate-180" />
      </td>

      <td className="px-1.5 py-1 align-middle">
        <RegionSelector
          size="sm"
          hideClear
          types={['city']}
          value={row.destination ? String(row.destination._id) : null}
          onChange={handleRegion('destination')}
          placeholder={t('sheet.row.destination_placeholder', {
            defaultValue: 'Destination',
          })}
          drawerTitle={t('sheet.row.destination_drawer_title', {
            defaultValue: 'Pick destination city',
          })}
          className="min-w-[130px]"
        />
      </td>

      <td className="px-1.5 py-1 align-middle">
        <VehicleTypeMultiSelector
          size="sm"
          hideClear
          value={row.vehicles}
          onChange={vehicles =>
            onChange({
              vehicles,
              // Temperature is implicit — chilled trucks/dynas/lorries/vans
              // imply a chilled lane; everything else falls back to dry. We
              // keep the field on the row so the eventual API payload still
              // satisfies `RouteCreateAPI.temperatureType`.
              temperatureType: deriveTemperatureFromVehicles(vehicles),
            })
          }
          placeholder={t('sheet.row.vehicles_placeholder', {
            defaultValue: 'Vehicles',
          })}
          className="min-w-[170px]"
        />
      </td>

      <td className="px-1.5 py-1 align-middle min-w-[110px]">
        <SheetCellSelect
          value={row.tripType}
          onChange={v => onChange({ tripType: v as TripType })}
          options={TRIP_TYPE_OPTIONS(t)}
          placeholder={t('m-route:form.trip_type.title')}
        />
      </td>

      <td className="px-1.5 py-1 align-middle min-w-[110px]">
        <SheetCellSelect
          value={row.loadType}
          onChange={v => onChange({ loadType: v as TripLoadType })}
          options={LOAD_TYPE_OPTIONS(t)}
          placeholder={t('m-route:form.load_type.title')}
        />
      </td>

      <td className="px-1.5 py-1 align-middle">
        <PriceEdit
          size="sm"
          tone={side === 'sell' ? 'positive' : 'info'}
          value={row.price || undefined}
          onChange={next => onChange({ price: next ?? 0 })}
          placeholder="0"
          className="w-[120px]"
        />
      </td>

      <td className="px-1.5 py-1 align-middle">
        <StatusBadge status={row.status} />
      </td>

      <td className="px-1.5 py-1 align-middle">
        <div className="flex items-center justify-end gap-0.5">
          <button
            type="button"
            onClick={() => deleteRow(row.id)}
            className="rounded-md p-1 text-gray-400 opacity-0 hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
            aria-label={t('c-main:crud_buttons.delete', { defaultValue: 'Delete' })}>
            <TrashIcon className="size-3" />
          </button>

          {/*
            Radix DropdownMenu portals to <body>, so the menu escapes the
            table's `overflow-x-auto` container and never gets clipped at
            the right edge — see screenshot from 2026-05-24. The trigger
            opacity stays in sync with the row hover via group classes.
          */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="rounded-md p-1 text-gray-400 opacity-0 hover:bg-gray-100 hover:text-gray-600 group-hover:opacity-100 data-[state=open]:opacity-100"
                aria-label={t('c-main:more', { defaultValue: 'More' })}>
                <MoreVerticalIcon className="size-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem
                className="text-xs"
                onSelect={() => duplicateRow(row.id)}>
                {t('sheet.row.duplicate', { defaultValue: 'Duplicate row' })}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-xs text-red-600 focus:bg-red-50 focus:text-red-700"
                onSelect={() => deleteRow(row.id)}>
                {t('sheet.row.delete', { defaultValue: 'Delete row' })}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  );
}

function StatusBadge({ status }: { status: SheetRow['status'] }) {
  const { t } = useTranslation(['m-pricing']);
  if (status === 'unchanged') return null;
  const map = {
    new: {
      label: t('sheet.status.new', { defaultValue: 'New' }),
      className: 'bg-emerald-100 text-emerald-700',
    },
    modified: {
      label: t('sheet.status.modified', { defaultValue: 'Edited' }),
      className: 'bg-amber-100 text-amber-700',
    },
    deleted: {
      label: t('sheet.status.deleted', { defaultValue: 'Removed' }),
      className: 'bg-red-100 text-red-700',
    },
  } as const;
  const m = map[status];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider',
        m.className,
      )}>
      {m.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Compact inline select — Radix `Select` primitives without `SelectAuto`'s
// `body1` typography class, so we get true 32 px triggers in the sheet.
// ---------------------------------------------------------------------------

interface SheetCellOption {
  value: string;
  label: string;
}

interface SheetCellSelectProps {
  value: string;
  onChange: (next: string) => void;
  options: SheetCellOption[];
  placeholder?: string;
}

function SheetCellSelect({ value, onChange, options, placeholder }: SheetCellSelectProps) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-full gap-1.5 rounded-md border-gray-300 bg-white px-2 py-0 text-xs font-normal text-gray-800 leading-none">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map(opt => (
          <SelectItem key={opt.value} value={opt.value} className="text-xs">
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

type TFn = (key: string, opts?: Record<string, unknown>) => string;

function TRIP_TYPE_OPTIONS(t: TFn): SheetCellOption[] {
  return [
    { value: 'one_way', label: t('m-route:trip_type.one_way', { defaultValue: 'One way' }) },
    {
      value: 'round_trip',
      label: t('m-route:trip_type.round_trip', { defaultValue: 'Round trip' }),
    },
  ];
}

function LOAD_TYPE_OPTIONS(t: TFn): SheetCellOption[] {
  return [
    { value: 'goods', label: t('m-route:load_type.goods', { defaultValue: 'Goods' }) },
    {
      value: 'container',
      label: t('m-route:load_type.container', { defaultValue: 'Container' }),
    },
  ];
}
